import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { database } from '@/firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import { useAuthStore } from './auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { track } from '@/analytics';

function durationBucket(ms) {
  if (ms < 5000) return 'under_5s';
  if (ms < 15000) return '5_to_15s';
  return 'over_15s';
}

function errorCode(e) {
  if (e?.code === 'functions/resource-exhausted') return 'daily_limit';
  if (e?.code === 'functions/unavailable') return 'busy';
  return 'other';
}

export const useStoryStore = defineStore('story', () => {
  const step = ref(1);

  const selectedGenre = ref([]);
  const selectedAudience = ref('adult');
  const selectedGender = ref('male');
  const selectedLanguage = ref('english');

  const generatedText = ref('');
  const generatedStory = ref('');
  const isGenerating = ref(false);
  const error = ref(null);

  const stories = ref([]);
  const isLoadingHistory = ref(false);

  const options = computed(() => ({
    genre: selectedGenre.value,
    audience: selectedAudience.value,
    gender: selectedGender.value,
    language: selectedLanguage.value,
  }));

  function composePromptText() {
    let text = `Write a story for ${selectedAudience.value} ${selectedGender.value} in ${selectedLanguage.value}.`;
    if (selectedGenre.value.length > 0) {
      text += ` The genre of the story is ${selectedGenre.value.join(', ')}.`;
    }
    return text;
  }

  function buildPromptText() {
    generatedText.value = composePromptText();
  }

  async function generateStory() {
    error.value = null;
    isGenerating.value = true;

    if (!generatedText.value) {
      buildPromptText();
    }

    // Only fixed menu choices and counts; never the prompt itself.
    track('story_requested', {
      audience: selectedAudience.value,
      language: selectedLanguage.value,
      genre_count: selectedGenre.value.length,
      prompt_edited: generatedText.value !== composePromptText() ? 'yes' : 'no',
    });
    const startedAt = Date.now();

    try {
      // The Gemini key lives server-side in a Firebase Cloud Function.
      // The client never sees it; model selection and retries happen there.
      const callGenerateStory = httpsCallable(functions, 'generateStory');
      const { data } = await callGenerateStory({ prompt: generatedText.value });
      const text = (data?.story || '').trim();
      if (!text) {
        error.value =
          'The story service returned an empty response. Please retry.';
        track('story_failed', { error_code: 'empty' });
        return;
      }
      generatedStory.value = text;
      track('story_generated', {
        model: data?.model || 'unknown',
        duration_bucket: durationBucket(Date.now() - startedAt),
      });
    } catch (e) {
      // HttpsError surfaces a user-safe message; fall back to a generic one.
      error.value = e?.message || 'Failed to generate story';
      track('story_failed', { error_code: errorCode(e) });
    } finally {
      isGenerating.value = false;
    }
  }

  async function saveStory(title) {
    const authStore = useAuthStore();
    const data = {
      user_id: authStore.user.uid,
      title,
      selectedGenre: selectedGenre.value,
      selectedAudiance: selectedAudience.value,
      selectedGender: selectedGender.value,
      selectedLanguage: selectedLanguage.value,
      generatedText: generatedText.value,
      story: generatedStory.value,
      timestamp: Date.now(),
    };
    await addDoc(collection(database, 'userStories'), data);
    track('story_saved');
  }

  async function loadStories() {
    const authStore = useAuthStore();
    if (!authStore.user) return;
    track('history_opened');
    isLoadingHistory.value = true;
    try {
      const q = query(
        collection(database, 'userStories'),
        where('user_id', '==', authStore.user.uid),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      stories.value = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (e) {
      error.value = e.message || 'Failed to load stories';
    } finally {
      isLoadingHistory.value = false;
    }
  }

  function resetWizard() {
    step.value = 1;
    selectedGenre.value = [];
    selectedAudience.value = 'adult';
    selectedGender.value = 'male';
    selectedLanguage.value = 'english';
    generatedText.value = '';
    generatedStory.value = '';
    error.value = null;
  }

  return {
    step,
    selectedGenre,
    selectedAudience,
    selectedGender,
    selectedLanguage,
    generatedText,
    generatedStory,
    isGenerating,
    error,
    stories,
    isLoadingHistory,
    options,
    buildPromptText,
    generateStory,
    saveStory,
    loadStories,
    resetWizard,
  };
});
